import dynamic from 'next/dynamic';
import { ComponentProps, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CardLocation from '@alltrails/amplitude/enums/CardLocation';
import TrailDetailsActionLocation from '@alltrails/amplitude/enums/TrailDetailsActionLocation';
import TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsAddToListButtonTapped from '@alltrails/amplitude/events/logTrailDetailsAddToListButtonTapped';
import { PageStrings } from '@alltrails/core/constants/pageStringHelpers';
import IconButton from '@alltrails/denali/components/IconButton';
import Heart from '@alltrails/denali/icons/Heart';
import HeartFilled from '@alltrails/denali/icons/HeartFilled';
import type { Size } from '@alltrails/denali/types';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import type TrailDetails from '@alltrails/types/TrailDetails';
import useSaveToListInstallPrompt from '@/hooks/useSaveToListInstallPrompt';

const MiniInstallInterstitial = dynamic(() => import('@alltrails/install-prompt/components/MiniInstallInterstitial'), { ssr: false });

type SaveCtaProps = {
  buttonLocation?: TrailDetailsActionLocation;
  elevated?: ComponentProps<typeof IconButton>['elevated'];
  size?: Size<'sm' | 'md'>;
  trailDetails: TrailDetails;
  trailId: number;
  trailPageType: TrailPageType;
  variant?: ComponentProps<typeof IconButton>['variant'];
};

const SaveCta = ({ buttonLocation, elevated, trailDetails, trailId, variant, size = 'md', trailPageType }: SaveCtaProps) => {
  const intl = useIntl();

  const reduxListsAnalyticsInfo = useMemo(
    () => ({
      detailedCardLocation: 'trail banner' as CardLocation,
      page: PageStrings.TRAIL_PAGE,
      trailPageType
    }),
    [trailPageType]
  );

  const { isFavorite, handleFavoriteClick, isSaveToastOpen, listModal, signUpModal } = useReduxListItems(
    undefined,
    false /* preventAutoInitialize */,
    reduxListsAnalyticsInfo
  );

  const { showSaveInstallPrompt, setShowSaveInstallPrompt } = useSaveToListInstallPrompt(isSaveToastOpen);

  const isTrailFavorite = isFavorite('trail', trailId);

  const onFavoriteClick = useCallback(() => {
    logTrailDetailsActionTapped({
      trail_id: trailId.toString(),
      trail_page_action: 'save',
      trail_page_location: buttonLocation,
      trail_page_type: trailPageType
    });
    logTrailDetailsAddToListButtonTapped({ trail_id: trailId });

    handleFavoriteClick('trail', trailId);
  }, [buttonLocation, handleFavoriteClick, trailId, trailPageType]);

  return (
    <>
      <IconButton
        elevated={elevated}
        icon={{ Component: isTrailFavorite ? HeartFilled : Heart }}
        onClick={onFavoriteClick}
        size={size}
        title={intl.formatMessage({ defaultMessage: 'Save' })}
        variant={variant}
      />
      {listModal}
      {signUpModal}
      {showSaveInstallPrompt && (
        <MiniInstallInterstitial
          buttonText={intl.formatMessage({ defaultMessage: 'Open in app' })}
          customAppPath="screen/saved"
          description={<FormattedMessage defaultMessage="Open the app to view and manage your saved adventures in one place." />}
          displayTrigger="save to list"
          featureIcon={{ Component: HeartFilled }}
          heading={<FormattedMessage defaultMessage="Access your saved trails anytime" />}
          isOpen={showSaveInstallPrompt}
          onRequestClose={() => setShowSaveInstallPrompt(false)}
          promptLocation="trail page"
          trailDetails={trailDetails}
          variant="feature-context"
        />
      )}
    </>
  );
};

export default SaveCta;
